import React, { useEffect } from "react";
import { useNavigate, Route, Routes } from "react-router-dom";
import { useAuthContext } from "../../Auth";
import { useOktaAuth } from "@okta/okta-react";

/*
 This is adapted from https://github.com/okta/okta-react/blob/4.1/src/SecureRoute.tsx with added functionality to check if the user has accepted the terms an conditions 
 and if the user is a member of the required groups to see the current route.
*/
const RequireAuth = ({ children, requiredGroups }) => {
  const pendingLogin = React.useRef(false);
  const { isUserInAnyGroups, termsAndConditionsAccepted, isAuthenticated } =
    useAuthContext();
  const { authState, oktaAuth } = useOktaAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authState.isAuthenticated) {
      if (!authState.isPending) {
        if (pendingLogin.current) {
          return;
        }
        pendingLogin.current = true;
        oktaAuth.setOriginalUri(); // This is the magic thing that makes Okta redirect the user to the original uri after login
        navigate(`/login`);
      }
    } else if (isAuthenticated && !termsAndConditionsAccepted) {
      if (!authState.isPending) {
        navigate("/termsandconditions");
      }
    }
  }, [
    authState.isAuthenticated,
    authState.isPending,
    navigate,
    isAuthenticated,
    oktaAuth,
    termsAndConditionsAccepted,
  ]);

  if (!isAuthenticated) {
    if (!authState.isPending) {
      return null;
    } else if (isAuthenticated && !termsAndConditionsAccepted) {
      if (!authState.isPending) {
        return null;
      }
      return null;
    } else if (isAuthenticated && !isUserInAnyGroups(requiredGroups)) {
      if (!authState.isPending) {
        return <h3>Unauthorized</h3>;
      }
      return null;
    }
  }

  return <React.Fragment>{children}</React.Fragment>;
};

const RouteWhenMemberOfAny = ({ component, groups, render, ...props }) => {
  const PassedComponent = component || (() => null);

  const element = render ? (
    render({
      component: (componentProps) => (
        <RequireAuth requiredGroups={groups}>
          <PassedComponent {...componentProps} />
        </RequireAuth>
      ),
    })
  ) : (
    <RequireAuth requiredGroups={groups}>
      <PassedComponent />
    </RequireAuth>
  );

  return (
    <Routes>
      <Route {...props} element={element} />
    </Routes>
  );
};

export default RouteWhenMemberOfAny;
